import { z } from "zod";

import {
  CanonicalBootstrapModelIdValues,
  CanonicalDuplicateAnalysisTypeValues,
  CanonicalDuplicateFixActionTypeValues,
  CanonicalDuplicateSourceTypeValues,
  CanonicalBootstrapValidationBlockerSourceTypeValues,
  CanonicalHierarchyRecommendedActionValues,
  CanonicalHierarchyRerunReasonValues,
  CanonicalHierarchyRunStatusValues,
  CanonicalTagAssetValidationIssueCodeValues,
  TaxonomyApprovalJobStatusValues,
  TaxonomyApprovalJobTypeValues,
  TaxonomyApprovalRequestItemStatusValues,
  CanonicalTagProposalStatusValues,
  LlmProviderValues,
  SourceTagRelevanceValues,
  TagAliasDecisionStatusValues,
  TagAliasMappingTypeValues,
  TagLayerValues,
  TagSourceValues,
  TaxonomyArtifactKindValues,
  TaxonomyProposalSourceValues,
  TaxonomyReviewItemTypeValues,
  TaxonomyReviewStatusValues,
  TaxonomyRunStatusValues,
  TaxonomyRunTypeValues,
  TaxonomyTriggerSourceValues,
  TaxonomyWorkspaceIdValues,
} from "../types/enums.js";
import { CanonicalTagSchema } from "./domain.js";

const NonEmptyStringSchema = z.string().min(1);
const IsoTimestampSchema = z.string().datetime({ offset: true });
const ConfidenceSchema = z.number().gte(0).lte(1);

export const TagAliasSchema = z
  .object({
    source: z.enum(TagSourceValues),
    rawKey: NonEmptyStringSchema,
    canonicalLayer: z.enum(TagLayerValues),
    canonicalSlug: NonEmptyStringSchema,
    mappingType: z.enum(TagAliasMappingTypeValues),
    mappingConfidence: ConfidenceSchema,
    sourceNamespace: NonEmptyStringSchema,
    sourceVersion: NonEmptyStringSchema,
    active: z.boolean(),
    notes: z.string(),
  })
  .strict();

export const TaxonomySnapshotMetadataSchema = z
  .object({
    artifactKind: z.enum(TaxonomyArtifactKindValues),
    version: NonEmptyStringSchema,
    publishedAt: IsoTimestampSchema,
    publishedBy: NonEmptyStringSchema,
    sourceRunId: NonEmptyStringSchema,
  })
  .strict();

export const CanonicalTagSnapshotSchema = z
  .object({
    metadata: TaxonomySnapshotMetadataSchema.extend({
      artifactKind: z.literal("CANONICAL_TAGS"),
    }),
    tags: z.array(CanonicalTagSchema),
  })
  .strict();

export const TagAliasSnapshotSchema = z
  .object({
    metadata: TaxonomySnapshotMetadataSchema.extend({
      artifactKind: z.literal("TAG_ALIASES"),
    }),
    aliases: z.array(TagAliasSchema),
  })
  .strict();

export const SourceTagCandidateKeySchema = z
  .object({
    source: z.enum(TagSourceValues),
    sourceNamespace: NonEmptyStringSchema,
    rawKey: NonEmptyStringSchema,
  })
  .strict();

export const SourceTagCandidateSchema = SourceTagCandidateKeySchema.extend({
  rawName: NonEmptyStringSchema,
  namespace: NonEmptyStringSchema,
  tagType: NonEmptyStringSchema,
  sourceStatus: NonEmptyStringSchema,
  relevance: z.enum(SourceTagRelevanceValues),
  category: z.boolean(),
  hasExemplaryTagging: z.boolean(),
  taggingCount: z.number().int().nonnegative(),
  description: z.string().optional(),
  sourceCreatedAt: IsoTimestampSchema.optional(),
  contentHash: NonEmptyStringSchema,
  lastSeenAt: IsoTimestampSchema,
  lastFetchedAt: IsoTimestampSchema,
  runId: NonEmptyStringSchema,
}).strict();

export const SourceTagEvidenceLineageTagSchema = z
  .object({
    rawKey: NonEmptyStringSchema,
    rawName: NonEmptyStringSchema,
    sourceNamespace: NonEmptyStringSchema,
    category: z.boolean(),
    tagType: NonEmptyStringSchema,
  })
  .strict();

export const SourceTagEvidenceCardSchema = z
  .object({
    oracleId: NonEmptyStringSchema,
    printingId: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    displayName: NonEmptyStringSchema,
    setCode: NonEmptyStringSchema,
    collectorNumber: NonEmptyStringSchema,
    oracleText: z.string().optional(),
    weight: NonEmptyStringSchema,
    status: NonEmptyStringSchema,
    annotation: z.string().nullable().optional(),
  })
  .strict();

export const SourceTagEvidenceSchema = SourceTagCandidateKeySchema.extend({
  rawName: NonEmptyStringSchema,
  description: z.string().optional(),
  ancestry: z.array(SourceTagEvidenceLineageTagSchema),
  childTags: z.array(SourceTagEvidenceLineageTagSchema),
  exemplarCards: z.array(SourceTagEvidenceCardSchema).max(10),
  totalTaggings: z.number().int().nonnegative(),
  descendantContextIncluded: z.boolean(),
  contentHash: NonEmptyStringSchema,
  lastSeenAt: IsoTimestampSchema,
  lastFetchedAt: IsoTimestampSchema,
  runId: NonEmptyStringSchema,
}).strict();

export const CanonicalTagProposalSchema = z
  .object({
    proposalId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    clusterKey: NonEmptyStringSchema,
    clusterLabel: NonEmptyStringSchema,
    proposalSource: z.enum(TaxonomyProposalSourceValues),
    sourceModel: z.string().optional(),
    sourceTagKeys: z.array(SourceTagCandidateKeySchema).min(1),
    layer: z.enum(TagLayerValues).optional(),
    slug: NonEmptyStringSchema.optional(),
    name: NonEmptyStringSchema.optional(),
    description: z.string().optional(),
    parentSlugs: z.array(NonEmptyStringSchema),
    synonyms: z.array(NonEmptyStringSchema),
    exampleOracleIds: z.array(NonEmptyStringSchema).max(5),
    confidence: ConfidenceSchema,
    needsReview: z.boolean(),
    reasoningSummary: z.string(),
    proposalStatus: z.enum(CanonicalTagProposalStatusValues),
    approvalValidationIssues: z
      .array(z.lazy(() => CanonicalTagAssetValidationIssueSchema))
      .optional(),
    approvalValidationBlockers: z
      .array(z.lazy(() => CanonicalBootstrapValidationBlockerSchema))
      .optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const CanonicalTagApprovalDraftSchema = z
  .object({
    layer: z.enum(TagLayerValues),
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    description: z.string().min(1),
    synonyms: z.array(NonEmptyStringSchema),
    exampleOracleIds: z.array(NonEmptyStringSchema).max(5),
  })
  .strict();

export const CanonicalBootstrapPendingProposalPageSchema = z
  .object({
    items: z.array(CanonicalTagProposalSchema),
    nextCursor: z.string().optional(),
  })
  .strict();

export const CanonicalBootstrapProposalMechanicSnippetSchema = z
  .object({
    cardLabel: NonEmptyStringSchema,
    text: NonEmptyStringSchema,
    sourceTagKey: NonEmptyStringSchema.optional(),
    sourceTagName: NonEmptyStringSchema.optional(),
  })
  .strict();

export const CanonicalBootstrapProposalFullTextCardSchema = z
  .object({
    cardLabel: NonEmptyStringSchema,
    oracleText: NonEmptyStringSchema,
    sourceTagKey: NonEmptyStringSchema.optional(),
    sourceTagName: NonEmptyStringSchema.optional(),
  })
  .strict();

export const CanonicalBootstrapProposalClusterProvenanceSchema = z
  .object({
    derivationType: z.enum([
      "single_raw_source_tag",
      "category_ancestor_cluster",
      "raw_source_tag_family_cluster",
      "merged_raw_source_tag_cluster",
    ]),
    derivedFromCategoryAncestor: z.boolean(),
    topSharedAncestors: z.array(
      z
        .object({
          rawKey: NonEmptyStringSchema,
          rawName: NonEmptyStringSchema,
          category: z.boolean(),
          sharedSourceTagCount: z.number().int().positive(),
        })
        .strict(),
    ),
  })
  .strict();

export const CanonicalBootstrapProposalSourceTagSummarySchema = z
  .object({
    rawKey: NonEmptyStringSchema,
    rawName: NonEmptyStringSchema,
    description: z.string().optional(),
    taggingCount: z.number().int().nonnegative(),
    category: z.boolean(),
  })
  .strict();

export const CanonicalBootstrapProposalLikelyProductRoleSchema = z
  .object({
    functionalMechanic: NonEmptyStringSchema,
    creatureTypeSynergy: NonEmptyStringSchema,
    deckArchetypeTheme: NonEmptyStringSchema,
  })
  .strict();

const CanonicalBootstrapProposalPromptContextBaseSchema = z
  .object({
    clusterProvenance:
      CanonicalBootstrapProposalClusterProvenanceSchema.optional(),
    sourceTagSummaries: z
      .array(CanonicalBootstrapProposalSourceTagSummarySchema)
      .optional(),
    likelyProductRole:
      CanonicalBootstrapProposalLikelyProductRoleSchema.optional(),
    mixedClusterSignals: z.array(NonEmptyStringSchema).optional(),
  })
  .strict();

export const CanonicalBootstrapProposalPromptContextSchema =
  z.discriminatedUnion("mode", [
    CanonicalBootstrapProposalPromptContextBaseSchema.extend({
      mode: z.literal("derived"),
      clusterKeywords: z.array(NonEmptyStringSchema),
      mechanicSnippets: z.array(
        CanonicalBootstrapProposalMechanicSnippetSchema,
      ),
    }).strict(),
    CanonicalBootstrapProposalPromptContextBaseSchema.extend({
      mode: z.literal("full_text_fallback"),
      fallbackReason: NonEmptyStringSchema.optional(),
      fullTextCards: z.array(CanonicalBootstrapProposalFullTextCardSchema),
    }).strict(),
  ]);

export const CanonicalBootstrapAwaitingApprovalExportProposalSchema = z
  .object({
    proposal: CanonicalTagProposalSchema,
    promptContext: CanonicalBootstrapProposalPromptContextSchema,
    supportingCards: z.array(SourceTagEvidenceCardSchema),
  })
  .strict();

export const CanonicalBootstrapAwaitingApprovalExportSchema = z
  .object({
    runId: NonEmptyStringSchema.optional(),
    items: z.array(CanonicalBootstrapAwaitingApprovalExportProposalSchema),
  })
  .strict();

export const CanonicalHierarchySynthesizedTagSchema = z
  .object({
    layer: z.enum(TagLayerValues),
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    description: z.string(),
    synonyms: z.array(NonEmptyStringSchema),
    parentSlugs: z.array(NonEmptyStringSchema),
    childProposalIds: z.array(NonEmptyStringSchema).min(1),
  })
  .strict();

export const CanonicalHierarchyParentTagSummarySchema = z
  .object({
    layer: z.enum(TagLayerValues),
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    description: z.string(),
  })
  .strict();

export const CanonicalHierarchySharedTaggerAncestorEvidenceSchema = z
  .object({
    rawKey: NonEmptyStringSchema,
    rawName: NonEmptyStringSchema,
    sourceNamespace: NonEmptyStringSchema,
    category: z.boolean(),
    tagType: NonEmptyStringSchema,
    targetSourceTagCount: z.number().int().positive(),
    candidateSourceTagCount: z.number().int().positive(),
  })
  .strict();

export const CanonicalHierarchyCandidateParentEvidenceSchema = z
  .object({
    parentTag: CanonicalHierarchyParentTagSummarySchema,
    sharedRawTags: z
      .array(CanonicalBootstrapProposalSourceTagSummarySchema)
      .default([]),
    sharedTaggerAncestors: z
      .array(CanonicalHierarchySharedTaggerAncestorEvidenceSchema)
      .default([]),
    tokenOverlap: z.array(NonEmptyStringSchema).default([]),
    existingChildCount: z.number().int().nonnegative().default(0),
    relatedTagCount: z.number().int().nonnegative().default(0),
    sourceTagCount: z.number().int().nonnegative().default(0),
    totalTaggings: z.number().int().nonnegative().default(0),
    productRoleHint: z.string().optional(),
  })
  .strict();

export const CanonicalHierarchySiblingHintSchema = z
  .object({
    proposalId: NonEmptyStringSchema,
    tag: CanonicalHierarchyParentTagSummarySchema,
    sharedRawTags: z
      .array(CanonicalBootstrapProposalSourceTagSummarySchema)
      .default([]),
    sharedTaggerAncestors: z
      .array(CanonicalHierarchySharedTaggerAncestorEvidenceSchema)
      .default([]),
    tokenOverlap: z.array(NonEmptyStringSchema).default([]),
    productRoleHint: z.string().optional(),
  })
  .strict();

export const CanonicalHierarchySynthesizedTagWithParentsSchema =
  CanonicalHierarchySynthesizedTagSchema.extend({
    parentTags: z.array(CanonicalHierarchyParentTagSummarySchema).default([]),
  }).strict();

export const CanonicalHierarchyProposalSchema = z
  .object({
    hierarchyProposalId: NonEmptyStringSchema,
    hierarchyRunId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    approvedSetHash: NonEmptyStringSchema,
    proposalSource: z.enum(TaxonomyProposalSourceValues),
    sourceModel: z.string().optional(),
    targetProposalId: NonEmptyStringSchema,
    targetTag: CanonicalTagSchema,
    sourceTagKeys: z.array(SourceTagCandidateKeySchema),
    sharedAncestors: z.array(SourceTagEvidenceLineageTagSchema),
    promptContext: CanonicalBootstrapProposalPromptContextSchema.optional(),
    candidateParents: z
      .array(CanonicalHierarchyParentTagSummarySchema)
      .default([]),
    candidateParentEvidence: z
      .array(CanonicalHierarchyCandidateParentEvidenceSchema)
      .default([]),
    candidateParentSlugs: z.array(NonEmptyStringSchema),
    siblingHints: z.array(CanonicalHierarchySiblingHintSchema).default([]),
    recommendedParents: z
      .array(CanonicalHierarchyParentTagSummarySchema)
      .default([]),
    recommendedParentSlugs: z.array(NonEmptyStringSchema),
    synthesizedParent:
      CanonicalHierarchySynthesizedTagWithParentsSchema.optional(),
    confidence: ConfidenceSchema,
    reasoningSummary: z.string(),
    proposalStatus: z.enum(CanonicalTagProposalStatusValues),
    approvalValidationIssues: z
      .array(z.lazy(() => CanonicalTagAssetValidationIssueSchema))
      .optional(),
    approvalValidationBlockers: z
      .array(z.lazy(() => CanonicalBootstrapValidationBlockerSchema))
      .optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const CanonicalHierarchyPendingProposalPageSchema = z
  .object({
    items: z.array(CanonicalHierarchyProposalSchema),
    nextCursor: z.string().optional(),
  })
  .strict();

export const CanonicalHierarchyAwaitingApprovalExportProposalSchema = z
  .object({
    proposal: CanonicalHierarchyProposalSchema,
  })
  .strict();

export const CanonicalHierarchyAwaitingApprovalExportSchema = z
  .object({
    runId: NonEmptyStringSchema.optional(),
    hierarchyRunId: NonEmptyStringSchema.optional(),
    items: z.array(CanonicalHierarchyAwaitingApprovalExportProposalSchema),
  })
  .strict();

export const CanonicalHierarchyDecisionSchema = z
  .object({
    hierarchyProposalId: NonEmptyStringSchema,
    hierarchyRunId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    decisionStatus: z.enum(TaxonomyReviewStatusValues),
    approvedParentSlugs: z.array(NonEmptyStringSchema).optional(),
    approvedSynthesizedParentParentSlugs: z
      .array(NonEmptyStringSchema)
      .optional(),
    notes: z.string(),
    decidedBy: z.string().optional(),
    decidedAt: IsoTimestampSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const CanonicalHierarchyRunSchema = z
  .object({
    hierarchyRunId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    approvedSetHash: NonEmptyStringSchema,
    runNumber: z.number().int().positive(),
    runStatus: z.enum(CanonicalHierarchyRunStatusValues),
    sourceModel: z.enum(CanonicalBootstrapModelIdValues).optional(),
    shouldRunAgain: z.boolean().optional(),
    recommendedAction: z
      .enum(CanonicalHierarchyRecommendedActionValues)
      .optional(),
    rerunReasonCodes: z.array(z.enum(CanonicalHierarchyRerunReasonValues)),
    rerunSummary: z.string().optional(),
    startedAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional(),
    failureReason: z.string().optional(),
    metrics: z.record(z.number().int().nonnegative()),
  })
  .strict();

export const CanonicalTagAssetValidationIssueSchema = z
  .object({
    code: z.enum(CanonicalTagAssetValidationIssueCodeValues),
    message: z.string().min(1),
    affectedCanonicalKeys: z.array(NonEmptyStringSchema).min(1),
  })
  .strict();

export const CanonicalBootstrapValidationBlockerTagSchema = z
  .object({
    layer: z.enum(TagLayerValues),
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    description: z.string(),
    parentSlugs: z.array(NonEmptyStringSchema),
    synonyms: z.array(NonEmptyStringSchema),
  })
  .strict();

export const CanonicalBootstrapValidationBlockerSourceSchema = z
  .object({
    sourceType: z.enum(CanonicalBootstrapValidationBlockerSourceTypeValues),
    proposalId: NonEmptyStringSchema.optional(),
    hierarchyProposalId: NonEmptyStringSchema.optional(),
    tag: CanonicalBootstrapValidationBlockerTagSchema,
  })
  .strict();

export const CanonicalBootstrapValidationBlockerSchema = z
  .object({
    issue: CanonicalTagAssetValidationIssueSchema,
    sources: z.array(CanonicalBootstrapValidationBlockerSourceSchema),
  })
  .strict();

export const CanonicalBootstrapValidationBlockerListSchema = z
  .object({
    runId: NonEmptyStringSchema.optional(),
    items: z.array(CanonicalBootstrapValidationBlockerSchema),
  })
  .strict();

export const CanonicalDuplicateTagSummarySchema = z
  .object({
    layer: z.enum(TagLayerValues),
    slug: NonEmptyStringSchema,
    name: z.string().min(1),
    description: z.string(),
    synonyms: z.array(NonEmptyStringSchema),
  })
  .strict();

export const CanonicalDuplicateSourceSchema = z
  .object({
    sourceKey: NonEmptyStringSchema,
    sourceType: z.enum(CanonicalDuplicateSourceTypeValues),
    proposalId: NonEmptyStringSchema.optional(),
    hierarchyProposalId: NonEmptyStringSchema.optional(),
    tag: CanonicalDuplicateTagSummarySchema,
    supportScore: z.number().int().nonnegative(),
    sourceRawTagKeys: z.array(NonEmptyStringSchema),
    clusterLabel: z.string().optional(),
    childProposalIds: z.array(NonEmptyStringSchema).optional(),
  })
  .strict();

export const CanonicalDuplicateFixRecommendationSchema = z
  .object({
    actionType: z.enum(CanonicalDuplicateFixActionTypeValues),
    keepSourceKey: NonEmptyStringSchema,
    rejectSourceKeys: z.array(NonEmptyStringSchema),
    confidence: ConfidenceSchema,
    summary: z.string().min(1),
  })
  .strict();

export const CanonicalDuplicateGroupSchema = z
  .object({
    groupId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    analysisType: z.enum(CanonicalDuplicateAnalysisTypeValues),
    layer: z.enum(TagLayerValues),
    reasonSummary: z.string().min(1),
    sources: z.array(CanonicalDuplicateSourceSchema).min(2),
    recommendation: CanonicalDuplicateFixRecommendationSchema,
  })
  .strict();

export const CanonicalDuplicateGroupListSchema = z
  .object({
    runId: NonEmptyStringSchema.optional(),
    items: z.array(CanonicalDuplicateGroupSchema),
  })
  .strict();

export const CanonicalDuplicateFixApplyResultSchema = z
  .object({
    runId: NonEmptyStringSchema,
    analysisType: z.enum(CanonicalDuplicateAnalysisTypeValues),
    appliedGroupCount: z.number().int().nonnegative(),
    approvedProposalCount: z.number().int().nonnegative(),
    rejectedProposalCount: z.number().int().nonnegative(),
    approvedHierarchyCount: z.number().int().nonnegative(),
    rejectedHierarchyCount: z.number().int().nonnegative(),
  })
  .strict();

export const TaxonomyValidationBlockedResultSchema = z
  .object({
    outcome: z.literal("BLOCKED_VALIDATION"),
    validationIssues: z.array(CanonicalTagAssetValidationIssueSchema),
    validationBlockers: z.array(CanonicalBootstrapValidationBlockerSchema),
  })
  .strict();

export const TaxonomyApprovalJobSchema = z
  .object({
    jobId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    jobType: z.enum(TaxonomyApprovalJobTypeValues),
    jobStatus: z.enum(TaxonomyApprovalJobStatusValues),
    queuedCount: z.number().int().nonnegative(),
    completedCount: z.number().int().nonnegative(),
    approvedCount: z.number().int().nonnegative().default(0),
    skippedInvalidCount: z.number().int().nonnegative().default(0),
    failedUnexpectedCount: z.number().int().nonnegative().default(0),
    nextCursor: z.string().optional(),
    startedBy: NonEmptyStringSchema,
    startedAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional(),
    failureReason: z.string().optional(),
  })
  .strict();

export const TaxonomyCleanupJobSchema = z
  .object({
    jobId: NonEmptyStringSchema,
    workspaceId: z.enum(TaxonomyWorkspaceIdValues),
    jobStatus: z.enum(TaxonomyApprovalJobStatusValues),
    targetRunIds: z.array(NonEmptyStringSchema),
    startedBy: NonEmptyStringSchema,
    startedAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional(),
    failureReason: z.string().optional(),
    deletedRunCount: z.number().int().nonnegative().optional(),
    deletedProposalCount: z.number().int().nonnegative().optional(),
    deletedDecisionCount: z.number().int().nonnegative().optional(),
    deletedReviewQueueCount: z.number().int().nonnegative().optional(),
    deletedCandidateCount: z.number().int().nonnegative().optional(),
    deletedEvidenceCount: z.number().int().nonnegative().optional(),
    deletedHierarchyRunCount: z.number().int().nonnegative().optional(),
    deletedHierarchyProposalCount: z.number().int().nonnegative().optional(),
    deletedHierarchyDecisionCount: z.number().int().nonnegative().optional(),
    deletedApprovalJobCount: z.number().int().nonnegative().optional(),
    deletedApprovalItemCount: z.number().int().nonnegative().optional(),
  })
  .strict();

export const TaxonomyApprovalRequestItemSchema = z.discriminatedUnion(
  "jobType",
  [
    z
      .object({
        jobId: NonEmptyStringSchema,
        runId: NonEmptyStringSchema,
        itemId: NonEmptyStringSchema,
        jobType: z.literal("CANONICAL_PROPOSALS"),
        proposalId: NonEmptyStringSchema,
        approvedTagDraft: CanonicalTagApprovalDraftSchema,
        createdAt: IsoTimestampSchema,
        itemStatus: z.enum(TaxonomyApprovalRequestItemStatusValues).optional(),
        processedAt: IsoTimestampSchema.optional(),
        failureReason: z.string().optional(),
        validationIssues: z
          .array(CanonicalTagAssetValidationIssueSchema)
          .optional(),
      })
      .strict(),
    z
      .object({
        jobId: NonEmptyStringSchema,
        runId: NonEmptyStringSchema,
        itemId: NonEmptyStringSchema,
        jobType: z.literal("CANONICAL_HIERARCHY"),
        hierarchyProposalId: NonEmptyStringSchema,
        createdAt: IsoTimestampSchema,
        itemStatus: z.enum(TaxonomyApprovalRequestItemStatusValues).optional(),
        processedAt: IsoTimestampSchema.optional(),
        failureReason: z.string().optional(),
        validationIssues: z
          .array(CanonicalTagAssetValidationIssueSchema)
          .optional(),
      })
      .strict(),
  ],
);

export const CanonicalTagDecisionSchema = z
  .object({
    proposalId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    decisionStatus: z.enum(TaxonomyReviewStatusValues),
    approvedTag: CanonicalTagSchema.optional(),
    approvedHierarchyParentOverrideSlugs: z
      .array(NonEmptyStringSchema)
      .optional(),
    notes: z.string(),
    decidedBy: z.string().optional(),
    decidedAt: IsoTimestampSchema.optional(),
    publishedVersion: z.string().optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const ApprovedCanonicalTagDecisionSchema =
  CanonicalTagDecisionSchema.extend({
    decisionStatus: z.literal("APPROVED"),
    approvedTag: CanonicalTagSchema,
  }).strict();

export const CanonicalTagApprovalMutationResultSchema = z.discriminatedUnion(
  "outcome",
  [
    z
      .object({
        outcome: z.literal("APPROVED"),
        decision: CanonicalTagDecisionSchema,
      })
      .strict(),
    TaxonomyValidationBlockedResultSchema,
  ],
);

export const CanonicalHierarchyApprovalMutationResultSchema =
  z.discriminatedUnion("outcome", [
    z
      .object({
        outcome: z.literal("APPROVED"),
        decision: CanonicalHierarchyDecisionSchema,
      })
      .strict(),
    TaxonomyValidationBlockedResultSchema,
  ]);

export const CanonicalTagHierarchyUpdateMutationResultSchema =
  z.discriminatedUnion("outcome", [
    z
      .object({
        outcome: z.literal("UPDATED"),
        decision: CanonicalTagDecisionSchema,
      })
      .strict(),
    TaxonomyValidationBlockedResultSchema,
  ]);

export const CanonicalHierarchySynthesizedParentUpdateMutationResultSchema =
  z.discriminatedUnion("outcome", [
    z
      .object({
        outcome: z.literal("UPDATED"),
        proposal: CanonicalHierarchyProposalSchema,
      })
      .strict(),
    TaxonomyValidationBlockedResultSchema,
  ]);

export const CanonicalBootstrapApprovedTagPageSchema = z
  .object({
    items: z.array(ApprovedCanonicalTagDecisionSchema),
    nextCursor: z.string().optional(),
  })
  .strict();

export const CanonicalBootstrapApprovedTagParentOptionSchema = z
  .object({
    layer: z.enum(TagLayerValues),
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    description: z.string().min(1),
    childCount: z.number().int().positive(),
  })
  .strict();

export const CanonicalBootstrapApprovedTagParentOptionListSchema = z
  .array(CanonicalBootstrapApprovedTagParentOptionSchema)
  .readonly();

export const TagAliasDecisionSchema = SourceTagCandidateKeySchema.extend({
  rawName: NonEmptyStringSchema,
  runId: NonEmptyStringSchema,
  proposalSource: z.enum(TaxonomyProposalSourceValues),
  sourceModel: z.string().optional(),
  decisionStatus: z.enum(TagAliasDecisionStatusValues),
  canonicalLayer: z.enum(TagLayerValues).optional(),
  canonicalSlug: NonEmptyStringSchema.optional(),
  mappingType: z.enum(TagAliasMappingTypeValues).optional(),
  mappingConfidence: ConfidenceSchema.optional(),
  sourceVersion: NonEmptyStringSchema.optional(),
  active: z.boolean(),
  notes: z.string(),
  reasoningSummary: z.string(),
  needsReview: z.boolean(),
  exampleOracleIds: z.array(NonEmptyStringSchema).max(5),
  reviewedBy: z.string().optional(),
  reviewedAt: IsoTimestampSchema.optional(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
}).strict();

export const TagAliasPendingDecisionPageSchema = z
  .object({
    items: z.array(TagAliasDecisionSchema),
    nextCursor: z.string().optional(),
  })
  .strict();

export const TaxonomySyncRunSchema = z
  .object({
    runId: NonEmptyStringSchema,
    runType: z.enum(TaxonomyRunTypeValues),
    runStatus: z.enum(TaxonomyRunStatusValues),
    triggerSource: z.enum(TaxonomyTriggerSourceValues),
    triggerDetail: z.string().optional(),
    pageLimit: z.number().int().positive().optional(),
    proposalModelId: z.enum(CanonicalBootstrapModelIdValues).optional(),
    captureLlmDebugArtifacts: z.boolean().optional(),
    startedAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional(),
    failureReason: z.string().optional(),
    warningCount: z.number().int().nonnegative().optional(),
    lastWarningAt: IsoTimestampSchema.optional(),
    metrics: z.record(z.number().int().nonnegative()),
  })
  .strict();

export const TaxonomyReviewQueueItemSchema = z
  .object({
    queueId: NonEmptyStringSchema,
    runId: NonEmptyStringSchema,
    itemType: z.enum(TaxonomyReviewItemTypeValues),
    itemRef: NonEmptyStringSchema,
    reviewStatus: z.enum(TaxonomyReviewStatusValues),
    title: NonEmptyStringSchema,
    summary: z.string(),
    impactScore: z.number().nonnegative(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const SourceTagObservationIndexSchema =
  SourceTagCandidateKeySchema.extend({
    oracleId: NonEmptyStringSchema,
    observedAt: IsoTimestampSchema,
  }).strict();

export const TaxonomyLlmModelPolicySchema = z
  .object({
    provider: z.enum(LlmProviderValues),
    modelId: NonEmptyStringSchema,
    useCase: NonEmptyStringSchema,
    order: z.number().int().nonnegative(),
    enabled: z.boolean(),
  })
  .strict();

export const TaxonomySnapshotObjectSchema = z
  .object({
    key: NonEmptyStringSchema,
    version: NonEmptyStringSchema,
    lastModified: IsoTimestampSchema.optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
  })
  .strict();

export const CanonicalBootstrapWorkspaceSummarySchema = z
  .object({
    runCount: z.number().int().nonnegative(),
    proposalCount: z.number().int().nonnegative(),
    decisionCount: z.number().int().nonnegative(),
    reviewQueueCount: z.number().int().nonnegative(),
    currentCandidateCount: z.number().int().nonnegative(),
    currentEvidenceCount: z.number().int().nonnegative(),
    hierarchyRunCount: z.number().int().nonnegative(),
    hierarchyProposalCount: z.number().int().nonnegative(),
    hierarchyDecisionCount: z.number().int().nonnegative(),
    approvalJobCount: z.number().int().nonnegative(),
    approvalItemCount: z.number().int().nonnegative(),
  })
  .strict();

export const CanonicalBootstrapAdminStateSchema = z
  .object({
    runs: z.array(TaxonomySyncRunSchema),
    hierarchyRuns: z.array(CanonicalHierarchyRunSchema),
    approvalJobs: z.array(TaxonomyApprovalJobSchema),
    cleanupJobs: z.array(TaxonomyCleanupJobSchema),
    validationBlockers: z.array(CanonicalBootstrapValidationBlockerSchema),
    workspace: CanonicalBootstrapWorkspaceSummarySchema,
    currentSnapshot: CanonicalTagSnapshotSchema.optional(),
    versionedOutputs: z.array(TaxonomySnapshotObjectSchema),
  })
  .strict();

export const TagAliasBootstrapWorkspaceSummarySchema = z
  .object({
    runCount: z.number().int().nonnegative(),
    currentCandidateCount: z.number().int().nonnegative(),
    currentEvidenceCount: z.number().int().nonnegative(),
    pendingDecisionCount: z.number().int().nonnegative(),
    autoApprovedDecisionCount: z.number().int().nonnegative(),
    approvedDecisionCount: z.number().int().nonnegative(),
    rejectedDecisionCount: z.number().int().nonnegative(),
  })
  .strict();

export const TagAliasBootstrapAdminStateSchema = z
  .object({
    runs: z.array(TaxonomySyncRunSchema),
    workspace: TagAliasBootstrapWorkspaceSummarySchema,
    currentCanonicalSnapshot: CanonicalTagSnapshotSchema.optional(),
    currentAliasSnapshot: TagAliasSnapshotSchema.optional(),
    versionedOutputs: z.array(TaxonomySnapshotObjectSchema),
  })
  .strict();

export const CleanupCanonicalBootstrapWorkspaceResultSchema = z
  .object({
    deletedRunCount: z.number().int().nonnegative(),
    deletedProposalCount: z.number().int().nonnegative(),
    deletedDecisionCount: z.number().int().nonnegative(),
    deletedReviewQueueCount: z.number().int().nonnegative(),
    deletedCandidateCount: z.number().int().nonnegative(),
    deletedEvidenceCount: z.number().int().nonnegative(),
    deletedHierarchyRunCount: z.number().int().nonnegative(),
    deletedHierarchyProposalCount: z.number().int().nonnegative(),
    deletedHierarchyDecisionCount: z.number().int().nonnegative(),
    deletedApprovalJobCount: z.number().int().nonnegative(),
    deletedApprovalItemCount: z.number().int().nonnegative(),
    cleanedAt: IsoTimestampSchema,
    cleanedBy: NonEmptyStringSchema,
  })
  .strict();
